import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
// 走子路徑而非 `import { isEqual } from 'lodash'`：lodash 主入口是 CommonJS，
// 具名 import 無法 tree-shake，會為了這一個函式把整包（約 71 KB）帶進 bundle。
import isEqual from 'lodash/isEqual';
import { PipesModule } from '../../pipes/pipes.module';
import { LocalStorageService } from '../../service/localstorage.service';
import { CSInputComponent } from '../cs-input/cs-input.component';

export interface ICSOption<T = string> {
  value: T;
  label?: string;
  icon?: string;
  img?: string;
  disabled?: boolean;
  data?: any;
  lightImg?: string;
}

@Component({
  selector: 'cs-select',
  standalone: true,
  imports: [CommonModule, CSInputComponent, PipesModule],
  templateUrl: './cs-select.component.html',
  styleUrls: ['./cs-select.component.scss'],
})
export class CSSelectComponent<T> implements OnChanges {
  @Input() options: ICSOption<T>[] = [];
  @Input() width = '100%';
  @Input() optionWidth: 'full' | 'auto' = 'full';
  @Input() value: T | null = null;
  @Input() disabled: boolean = false;
  @Input() selectOnPointerDown = false;
  /** Prevents interaction while preserving the select appearance. Intended for display-only selects such as KYC country code. */
  @Input() locked: boolean = false;
  readonly defaultPlaceholderKey = 'select_an_option';
  readonly defaultSearchPlaceholderKey = 'search_country';
  @Input() placeholder: string = this.defaultPlaceholderKey;
  @Input() translatePlaceholder = false;
  @Input() searchPlaceholder: string = this.defaultSearchPlaceholderKey;
  @Input() translateSearchPlaceholder = false;
  @Input() size: 'default' | 'small' = 'default';

  @Input() searchTarget: ('label' | 'value' | 'data')[] = [];
  @Input() searchDataKey: string[] = [];
  /** Searches from the beginning by default; consumers may opt into contains matching when needed. */
  @Input() searchMatchMode: 'contains' | 'startsWith' = 'startsWith';

  @Input() marginBottom: string | undefined = '0';

  @Output() valueChange = new EventEmitter<T | null>();

  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;
  @ViewChild('triggerButton') triggerButton!: ElementRef;
  @ContentChild('optionTemplate', { static: false }) optionTemplate?: TemplateRef<any>;
  @ContentChild('selectedTemplate', { static: false }) selectedTemplate?: TemplateRef<any>;

  @HostBinding('style.width') get hostWidth() {
    return this.width;
  }

  // 偵測 window scroll 事件，若選單開啟則關閉
  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event): void {
    if (this.isOpen && !this.disableScrollClose) {
      this.closeDropdown();
    }
  }

  overlayRef!: OverlayRef;

  isOpen = false;

  filterOptions: ICSOption<T>[] = [];

  selectedOption?: ICSOption<T>;

  searchKeyWord: string = '';

  isLight: boolean = this.localStorageService.theme === 'sun';

  // Skip closing on scroll while interacting with the search input (mobile keyboard can trigger scroll)
  private disableScrollClose = false;

  constructor(
    public localStorageService: LocalStorageService,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { options, value, locked } = changes;

    if (locked?.currentValue) {
      this.closeDropdown();
    }

    if (options || value) {
      const currentOptions = Array.isArray(options?.currentValue)
        ? options.currentValue
        : Array.isArray(this.options)
          ? this.options
          : [];

      const currentValue = value?.currentValue ?? this.value;

      this.selectedOption = currentOptions.find((opt: ICSOption<T>) => isEqual(opt.value, currentValue));
      this.filterOptions = [...currentOptions];
    }
  }

  openDropdown() {
    if (this.disabled || this.locked) {
      return;
    }

    if (this.overlayRef) {
      this.closeDropdown();
      return;
    }

    const buttonRect = this.triggerButton.nativeElement.getBoundingClientRect();

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.triggerButton.nativeElement)
      .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]);

    const options: any = {
      hasBackdrop: true,
      backdropClass: 'transparent-backdrop',
      positionStrategy,
    };

    if (this.optionWidth === 'full') {
      options['scrollStrategy'] = this.overlay.scrollStrategies.reposition();
      options['width'] = buttonRect.width;
    }

    this.overlayRef = this.overlay.create(options);

    this.overlayRef.backdropClick().subscribe(() => this.closeDropdown());

    const portal = new TemplatePortal(this.dropdownTemplate, this.vcr);
    this.overlayRef.attach(portal);
    this.isOpen = true;
  }

  onSearchFocus() {
    this.disableScrollClose = true;
  }

  onSearchBlur() {
    this.disableScrollClose = false;
  }

  search(e: any) {
    const input = String(e ?? '')
      .trim()
      .toLowerCase();
    this.filterOptions = this.options.filter(option => {
      return this.searchTarget.some(target => {
        if (target === 'label') {
          return this.matchesSearch(option.label, input);
        }

        if (target === 'value') {
          return this.matchesSearch(String(option.value), input);
        }

        if (target === 'data' && option.data) {
          return this.searchDataKey.some(key => this.matchesSearch(String(option.data[key] || ''), input));
        }

        return false;
      });
    });
  }

  private matchesSearch(value: string | undefined, input: string): boolean {
    const normalizedValue = value?.toLowerCase() || '';
    return this.searchMatchMode === 'startsWith' ? normalizedValue.startsWith(input) : normalizedValue.includes(input);
  }

  closeDropdown() {
    this.disableScrollClose = false;
    this.overlayRef?.dispose();
    this.overlayRef = undefined!;
    this.isOpen = false;
  }

  select(option: ICSOption<T>) {
    if (this.locked || option.disabled) {
      return;
    }

    if (isEqual(option.value, this.value)) {
      this.closeDropdown();
      return;
    }

    // Keep the trigger label in sync even when the consumer's two-way value
    // binding does not produce a new input change during this click cycle.
    this.selectedOption = option;
    this.value = option.value;
    this.valueChange.emit(option.value);
    this.closeDropdown();
  }

  onOptionPointerDown(event: PointerEvent, option: ICSOption<T>): void {
    if (!this.selectOnPointerDown) return;

    event.preventDefault();
    event.stopPropagation();
    this.selectedOption = option;
    this.select(option);
  }
}
